import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  Divider,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import PaymentsIcon from '@mui/icons-material/Payments';
import SaveIcon from '@mui/icons-material/Save';
import { useNavigate, useParams } from 'react-router-dom';
import { SMART_COLORS, smartCardSx } from '../../theme/smartStableTheme';

const apiUrl = process.env.REACT_APP_API_URL;

const EditReproductionProcedure = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    procedure_name: '',
    description: '',
    default_price: '',
  });
  const [loadingError, setLoadingError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchProcedure = async () => {
      try {
        const response = await axios.get(`${apiUrl}/reproduction-clinic/procedures/${id}`);
        setFormData(response.data);
      } catch (error) {
        console.error('Error fetching reproduction procedure:', error);
        setLoadingError('Unable to load procedure details.');
      }
    };

    fetchProcedure();
  }, [id]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${apiUrl}/reproduction-clinic/procedures/${id}`, {
        ...formData,
        default_price: formData.default_price || 0,
        user_id: localStorage.getItem('userId'),
      });

      navigate('/ReproductionProceduresList');
    } catch (error) {
      console.error('Error updating reproduction procedure:', error);
      setLoadingError('Unable to save procedure changes.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box className="smart-page-container" sx={{ maxWidth: 'var(--app-page-max)', mx: 'auto' }}>
      <Paper
        elevation={0}
        sx={{
          ...smartCardSx,
          p: { xs: 2.5, md: 3 },
          mb: 3,
          display: 'flex',
          justifyContent: 'space-between',
          gap: 2,
          alignItems: { xs: 'stretch', md: 'center' },
          flexDirection: { xs: 'column', md: 'row' },
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Box
            sx={{
              width: 52,
              height: 52,
              borderRadius: '50%',
              bgcolor: SMART_COLORS.roseSoft,
              color: SMART_COLORS.rose,
              display: 'grid',
              placeItems: 'center',
            }}
          >
            <FavoriteBorderIcon />
          </Box>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 900, color: SMART_COLORS.text }}>
              Edit Reproduction Procedure
            </Typography>
            <Typography sx={{ color: SMART_COLORS.muted }}>
              Update the procedure label, notes, and accounting default.
            </Typography>
          </Box>
        </Stack>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/ReproductionProceduresList')}
          sx={{ borderColor: SMART_COLORS.line, color: SMART_COLORS.text, fontWeight: 800 }}
        >
          Back
        </Button>
      </Paper>

      <Paper elevation={0} sx={{ ...smartCardSx, p: { xs: 2.5, md: 3.5 } }}>
        {loadingError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {loadingError}
          </Alert>
        )}
        <Box component="form" onSubmit={handleSubmit}>
          <Stack spacing={2.5}>
            <TextField
              label="Procedure name"
              fullWidth
              required
              value={formData.procedure_name || ''}
              onChange={(e) => setFormData({ ...formData, procedure_name: e.target.value })}
            />
            <TextField
              label="Description"
              fullWidth
              multiline
              rows={4}
              value={formData.description || ''}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
            <TextField
              label="Default price"
              fullWidth
              type="number"
              value={formData.default_price || ''}
              onChange={(e) => setFormData({ ...formData, default_price: e.target.value })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PaymentsIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: <InputAdornment position="end">SAR</InputAdornment>,
              }}
            />
            <Divider />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="flex-end">
              <Button
                variant="outlined"
                onClick={() => navigate('/ReproductionProceduresList')}
                sx={{ borderColor: SMART_COLORS.line, color: SMART_COLORS.text, fontWeight: 800 }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={saving}
                startIcon={<SaveIcon />}
                sx={{ bgcolor: SMART_COLORS.gold, fontWeight: 900, '&:hover': { bgcolor: SMART_COLORS.goldHover } }}
              >
                Save changes
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Paper>
    </Box>
  );
};

export default EditReproductionProcedure;
